// ============================================================================
// AI completion — Production: Supabase ai-cover-letter Edge Function only (signed-in).
// Development: optional VITE_AI_* browser shortcut (explicitly guarded; never bundled in prod chunks).
// Extension / web must never ship AI provider secrets; anon + JWT call Edge Function only.
// ============================================================================

import { supabase, supabaseConfigured } from '../storage/supabase'

const devBrowserAiAllowed =
  import.meta.env.DEV === true && import.meta.env.PROD !== true

const LEGACY_KEY =
  devBrowserAiAllowed &&
  typeof import.meta.env.VITE_AI_API_KEY === 'string' &&
  import.meta.env.VITE_AI_API_KEY.length > 0
    ? import.meta.env.VITE_AI_API_KEY
    : ''
const LEGACY_URL =
  typeof import.meta.env.VITE_AI_API_URL === 'string' && import.meta.env.VITE_AI_API_URL.length > 0
    ? import.meta.env.VITE_AI_API_URL
    : 'https://api.openai.com/v1/chat/completions'
const LEGACY_MODEL =
  typeof import.meta.env.VITE_AI_MODEL === 'string' && import.meta.env.VITE_AI_MODEL.length > 0
    ? import.meta.env.VITE_AI_MODEL
    : 'gpt-4o-mini'

export class AiCompletionTransport {
  /**
   * Signed-in flows always use `functions.invoke('ai-cover-letter')` — no provider keys in-process.
   * Dev fallback calls OpenAI-compat HTTP only when DEV + `VITE_AI_API_KEY`; production forbids both.
   */
  async completeCoverLetterPrompt(prompt: string): Promise<string> {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (supabaseConfigured && session) {
      const { data, error } = await supabase.functions.invoke<{ content?: string; error?: string }>(
        'ai-cover-letter',
        { body: { prompt } },
      )
      if (error) {
        throw new Error(error.message || 'AI edge function failed')
      }
      if (data?.error) {
        throw new Error(data.error)
      }
      if (data?.content) {
        return data.content
      }
      throw new Error('AI edge function returned no content')
    }

    if (!devBrowserAiAllowed || import.meta.env.PROD) {
      throw new Error(
        'Sign in to use AI. Calls go to the ai-cover-letter Edge Function; the extension/client never holds the provider secret.',
      )
    }

    if (LEGACY_KEY) {
      return this.completeViaBrowserBundledKey(prompt)
    }

    throw new Error(
      'Dev: sign in so requests use ai-cover-letter, ' +
        'or set VITE_AI_API_KEY for local insecure testing (bundled — never for production builds).',
    )
  }

  isAvailable(): boolean {
    const canUseEdge = !!supabaseConfigured
    const devShortcut = !!(devBrowserAiAllowed && LEGACY_KEY)
    return canUseEdge || devShortcut
  }

  modelLabel(): string {
    if (!devBrowserAiAllowed || !LEGACY_KEY) {
      return 'ai-cover-letter (edge)'
    }
    return LEGACY_MODEL
  }

  private async completeViaBrowserBundledKey(prompt: string): Promise<string> {
    const response = await fetch(LEGACY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${LEGACY_KEY}`,
      },
      body: JSON.stringify({
        model: LEGACY_MODEL,
        messages: [
          {
            role: 'system',
            content:
              'You are an expert cover letter writer who creates compelling, personalized cover letters.',
          },
          { role: 'user', content: prompt },
        ],
        temperature: 0.7,
        max_tokens: 800,
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`AI API error (${response.status}): ${errorText}`)
    }

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>
    }
    const content = data.choices?.[0]?.message?.content
    if (!content) {
      throw new Error('AI API returned an empty response')
    }
    return content.trim()
  }
}

export const aiCompletionTransport = new AiCompletionTransport()
